/**
 * DailySalesGrid — Britto's 3 cities × 4 platforms daily sales entry.
 *
 * Reads/writes:
 *   GET  /api/tdb-mis/sales/daily?date=YYYY-MM-DD
 *   PUT  /api/tdb-mis/sales/daily   (date, city, channel, amount)
 *
 * Shopify "Online" column is auto-attributed by routing rules
 * (`tdb_city_routing_rules`) and rendered read-only. Unallocated
 * Shopify revenue surfaces as a red banner with deep-link to the
 * resolution UI.
 *
 * UX:
 *   • Tab cycles through cells left-to-right, top-to-bottom
 *   • Save on blur (no submit button — feels like a spreadsheet)
 *   • Toast on success/failure
 *   • Date picker at top — defaults to today (IST)
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, Save, AlertTriangle, ShoppingCart, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

const CITIES = ['Bengaluru', 'Mumbai', 'Gurgaon'];
const MANUAL_CHANNELS = [
  { channel: 'Outlet', label: 'Outlet' },
  { channel: 'B2B',    label: 'B2B' },
  { channel: 'Zomato', label: 'Zomato' },
  { channel: 'Swiggy', label: 'Swiggy' },
];

// IST today as YYYY-MM-DD
function todayIST() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 10);
}

function fmtRupees(n) {
  if (!n) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export default function DailySalesGrid({ className = '' }) {
  const [date, setDate] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [err, setErr] = useState('');

  // Cell drafts — keyed "city|channel"
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/sales/daily', { params: { date } });
      setData(r.data);
      // Seed drafts from server values
      const next = {};
      for (const e of r.data.manual_entries || []) {
        next[`${e.city}|${e.channel}`] = String(e.amount || 0);
      }
      setDrafts(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load sales grid');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const saveCell = useCallback(async (city, channel) => {
    const key = `${city}|${channel}`;
    const raw = drafts[key] ?? '';
    const amount = parseFloat(raw) || 0;
    setSavingKey(key);
    setErr('');
    try {
      await api.put('/tdb-mis/sales/daily', { date, city, channel, amount });
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? '' : k)), 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || `Failed to save ${key}`);
    } finally {
      setSavingKey('');
    }
  }, [drafts, date]);

  const shopifyByCity = data?.shopify_by_city || {};
  const unallocated = data?.shopify_unallocated || 0;

  const totals = useMemo(() => {
    const totalPerCity = {};
    const totalPerChannel = {};
    let grand = 0;
    for (const city of CITIES) {
      let cityTotal = 0;
      for (const { channel } of MANUAL_CHANNELS) {
        const v = parseFloat(drafts[`${city}|${channel}`]) || 0;
        cityTotal += v;
        totalPerChannel[channel] = (totalPerChannel[channel] || 0) + v;
      }
      cityTotal += shopifyByCity[city] || 0;
      totalPerCity[city] = cityTotal;
      grand += cityTotal;
    }
    return { totalPerCity, totalPerChannel, grand };
  }, [drafts, shopifyByCity]);

  return (
    <div
      data-testid="daily-sales-grid"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Daily Sales Grid</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            3 cities × 5 channels · auto-saves
          </span>
        </div>
        <input
          data-testid="daily-sales-date-picker"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          max={todayIST()}
        />
      </div>

      {unallocated > 0 && (
        <div
          data-testid="daily-sales-unallocated-banner"
          className="mb-3 p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900 flex items-start gap-1.5"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">{fmtRupees(unallocated)} Shopify revenue unallocated</span> ·
            no routing rule matched. Resolve at the bottom of this tile.
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="daily-sales-table">
          <thead>
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">Channel</th>
              {CITIES.map((c) => (
                <th key={c} className="text-right py-2 px-2 font-medium">{c}</th>
              ))}
              <th className="text-right py-2 px-2 font-medium bg-gray-50 rounded-tr-lg">Total</th>
            </tr>
          </thead>
          <tbody>
            {MANUAL_CHANNELS.map(({ channel, label }) => (
              <tr key={channel} className="border-t border-gray-100">
                <td className="py-2 px-2 font-medium text-gray-800">{label}</td>
                {CITIES.map((city) => {
                  const key = `${city}|${channel}`;
                  const saving = savingKey === key;
                  const saved = savedKey === key;
                  return (
                    <td key={city} className="text-right py-1 px-1 align-middle">
                      <div className="relative">
                        <input
                          data-testid={`daily-sales-cell-${city.toLowerCase()}-${channel.toLowerCase()}`}
                          type="number"
                          inputMode="numeric"
                          min="0"
                          step="1"
                          value={drafts[key] ?? ''}
                          onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                          onBlur={() => saveCell(city, channel)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                          }}
                          disabled={loading}
                          placeholder="0"
                          className="w-full text-right text-sm px-2 py-1.5 rounded-md border border-gray-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 outline-none"
                        />
                        {saving && <span className="absolute -top-1 -right-1 text-[9px] text-gray-500">…</span>}
                        {saved && (
                          <CheckCircle2 className="w-3 h-3 absolute -top-1 -right-1 text-emerald-600" />
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="text-right py-2 px-2 align-middle bg-gray-50 font-semibold text-gray-900">
                  {fmtRupees(totals.totalPerChannel[channel] || 0)}
                </td>
              </tr>
            ))}
            {/* Shopify auto row (read-only) */}
            <tr className="border-t border-gray-100 bg-blue-50/30">
              <td className="py-2 px-2 font-medium text-gray-800 flex items-center gap-1">
                <ShoppingCart className="w-3.5 h-3.5 text-blue-700" /> Shopify (auto)
              </td>
              {CITIES.map((city) => (
                <td key={city} data-testid={`daily-sales-shopify-${city.toLowerCase()}`} className="text-right py-2 px-2 text-gray-700">
                  {fmtRupees(shopifyByCity[city] || 0)}
                </td>
              ))}
              <td className="text-right py-2 px-2 bg-gray-50 font-semibold text-gray-900">
                {fmtRupees(CITIES.reduce((s, c) => s + (shopifyByCity[c] || 0), 0))}
              </td>
            </tr>

            {/* Grand total row */}
            <tr className="border-t-2 border-gray-300 bg-gray-50">
              <td className="py-2 px-2 font-bold text-gray-900">Total</td>
              {CITIES.map((city) => (
                <td key={city} data-testid={`daily-sales-total-${city.toLowerCase()}`} className="text-right py-2 px-2 font-bold text-gray-900">
                  {fmtRupees(totals.totalPerCity[city] || 0)}
                </td>
              ))}
              <td data-testid="daily-sales-grand-total" className="text-right py-2 px-2 font-bold text-emerald-700">
                {fmtRupees(totals.grand)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {err && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {err}
        </div>
      )}

      <div className="mt-2 text-[11px] text-gray-500">
        <Save className="w-3 h-3 inline mr-1" />
        Tab through cells · saves on blur · audit log captures every change
      </div>
    </div>
  );
}
